<template>
  <div
    class="v2-wishlist-card"
    @click="goToWishlist()"
  >
    <div class="v2-wishlist-card__image">
      <img
        :src="wishlist.collectionLogo || defaultCollectionImage"
        :alt="wishlist.name"
        class="v2-wishlist-card__img"
        @error="onImageError"
      >
    </div>
    <div
      v-if="!isDashboardPage"
      class="v2-wishlist-card__actions"
      @click.stop
    >
      <v2-dropdown
        :options="dropdownActions"
        :placeholder="''"
        label-key="label"
        value-key="key"
        align="right"
        @select="onDropdownAction"
      >
        <template #toggle>
          <button class="v2-wishlist-card__menu-btn">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <circle
                cx="12"
                cy="5"
                r="1"
              />
              <circle
                cx="12"
                cy="12"
                r="1"
              />
              <circle
                cx="12"
                cy="19"
                r="1"
              />
            </svg>
          </button>
        </template>
      </v2-dropdown>
    </div>
    <div class="v2-wishlist-card__body">
      <div class="v2-flex v2-justify-between v2-items-center">
        <div
          class="v2-wishlist-card__name"
          :title="wishlist.name"
        >
          {{ wishlist.name }}
        </div>
        <span
          v-if="isBrand && !wishlist.sharedDate"
          class="v2-wishlist-card__share-btn"
          @click.stop="onDropdownAction('share')"
        >
          <svg
            width="20"
            height="16"
            viewBox="0 0 23 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M21.2025 2.17499C21.2025 2.09999 21.2025 2.02499 21.1103 1.94999V1.87499C21.1103 1.79999 21.0181 1.72499 20.926 1.64999C20.8799 1.61249 20.8338 1.59374 20.7877 1.57499C20.7416 1.55624 20.6955 1.53749 20.6494 1.49999H20.5572C20.465 1.49999 20.3729 1.49999 20.2807 1.42499V1.49999C20.1885 1.49999 20.0963 1.49999 20.0041 1.57499L1.56717 6.82499C1.19843 6.89999 0.921875 7.12499 0.921875 7.49999C0.921875 7.79999 1.10624 8.09999 1.47498 8.24999L9.40287 11.1L12.9059 17.55C13.0903 17.85 13.3668 18 13.7356 18C14.1043 18 14.473 17.775 14.5652 17.475L21.0181 2.47499C21.2025 2.39999 21.2025 2.32499 21.2025 2.17499ZM16.7776 4.04999L9.95598 9.59999L4.33271 7.57499L16.7776 4.04999ZM11.2466 10.65L13.7356 15.225L18.0682 5.09999L11.2466 10.65Z"
              fill="currentColor"
            />
          </svg>
        </span>
      </div>
      <div class="v2-wishlist-card__count v2-mt-1">
        {{ wishlist.count || 0 }} product{{ wishlist.count > 1 ? 's' : '' }}
      </div>
      <v2-badge
        v-if="wishlist.collectionType"
        :variant="getCollectionBadgeVariant(wishlist.collectionType)"
        class="v2-mt-1"
      >
        {{ getCollectionTypeText(wishlist.collectionType) }}
      </v2-badge>
      <v2-badge
        v-if="isDashboardPage && isRetailer"
        :variant="wishlist.isBrandInitiated ? 'primary' : 'default'"
        class="v2-mt-1"
      >
        {{ wishlist.isBrandInitiated ? 'Shared with me' : 'Created by me' }}
      </v2-badge>
      <div class="v2-wishlist-card__date v2-mt-1">
        {{ parseCollectionDate(wishlist.collectionExpiry) }}
      </div>
      <div
        v-if="isBrand && (isEntityAdmin || isSalesManager) && wishlist.salesRep"
        class="v2-wishlist-card__meta"
      >
        {{ wishlist.isCreatedBySelf ? selfOption.name : 'Sales rep: ' + wishlist.salesRep }}
      </div>
      <div
        v-if="isBrand"
        class="v2-wishlist-card__meta"
      >
        <span v-if="wishlist.sharedDate">
          Shared with: {{ wishlist.retailerName || wishlist.email }}
        </span>
        <span v-else>Not shared yet</span>
      </div>
      <div class="v2-wishlist-card__meta">
        <span v-if="wishlist.sharedDate && isBrand">
          Shared: {{ formattedDate(wishlist.sharedDate) }}
        </span>
        <span v-else>
          Created: {{ formattedDate(wishlist.createdAt) }}
        </span>
      </div>
      <div class="v2-wishlist-card__meta">
        Updated: {{ formattedDate(wishlist.updatedAt) }}
      </div>
    </div>
  </div>
</template>

<script>
import {
  parseCollectionDate,
  getCollectionTypeText,
  getDefaultProductImage,
} from '@/@core/utils/utils'
import { utils } from '@kingpin-global/kingpin-utils-frontend'
import { getSelfOption } from '@/service/user.service'
import analytics from '@/@core/utils/analytics'
import constants from '@/constants'
import V2Badge from '@/components/v2/V2Badge.vue'
import V2Dropdown from '@/components/v2/V2Dropdown.vue'
import UserRoleMixin from '../../apps/UserRoleMixin.vue'

const { formattedDate } = utils

export default {
  name: 'V2WishlistCard',
  components: {
    V2Badge,
    V2Dropdown,
  },
  mixins: [UserRoleMixin],
  props: {
    wishlist: {
      type: Object,
      default: () => ({}),
    },
    isDashboardPage: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      defaultCollectionImage: getDefaultProductImage(300, 300),
      formattedDate,
      parseCollectionDate,
      getCollectionTypeText,
      selfOption: getSelfOption(),
    }
  },
  computed: {
    dropdownActions() {
      const actions = [
        { key: 'rename', label: 'Rename' },
        { key: 'download', label: 'Download' },
      ]
      if (this.isBrand) {
        actions.push({ key: 'share', label: 'Share' })
        actions.push({ key: 'share-link', label: 'Share public link' })
      }
      actions.push({ key: 'delete', label: 'Delete' })
      return actions
    },
  },
  methods: {
    onImageError(event) {
      event.target.src = this.defaultCollectionImage
    },
    getCollectionBadgeVariant(type) {
      if (!type) return 'default'
      return 'default'
    },
    onDropdownAction(action) {
      this.$emit('card-action', { action, wishlist: this.wishlist })
    },
    goToWishlist() {
      this.$router.push({
        name: this.isRetailer ? 'v2-wishlist-products' : 'v2-selection-products',
        params: { wishlistId: this.wishlist._id },
      })
      if (this.isDashboardPage) {
        analytics.track(
          constants.TRACKS.ACTIONS.USER_CLICKS_A_WISHLIST_ON_DASHBOARD,
          { wishlist: this.wishlist },
        )
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-wishlist-card {
  position: relative;
  // Tinted glass without backdrop-filter — see V2ProductCard / migration
  // plan note #3 for rationale (per-card blur in dense grids causes scroll
  // jitter). Page mesh substrate provides the frosted look.
  background: var(--v2-glass-surface-1);
  border: 1px solid var(--v2-glass-stroke);
  border-radius: var(--v2-radius-lg);
  box-shadow: var(--v2-glass-shadow);
  cursor: pointer;
  contain: layout;
  transition: box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease;

  &:hover {
    border-color: var(--v2-glass-stroke-strong);
    transform: translateY(-1px);
    z-index: 1;
  }

  &__image {
    position: relative;
    overflow: hidden;
    background: var(--v2-muted);
    border-top-left-radius: var(--v2-radius-lg);
    border-top-right-radius: var(--v2-radius-lg);
  }

  &__img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__actions {
    position: absolute;
    top: var(--v2-space-2);
    right: var(--v2-space-2);
    z-index: 10;
    display: flex;
    align-items: center;
    gap: var(--v2-space-1);
  }

  &__share-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    cursor: pointer;
    color: var(--v2-muted-foreground);
    transition: color 0.15s ease;

    &:hover {
      color: var(--v2-primary);
    }
  }

  &__menu-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    background: var(--v2-card);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
    cursor: pointer;
    color: var(--v2-foreground);

    &:hover {
      background: var(--v2-muted);
    }
  }

  &__body {
    padding: var(--v2-space-3) var(--v2-space-4);
  }

  &__name {
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--v2-foreground);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 200px;
  }

  &__count {
    font-size: 0.75rem;
    color: var(--v2-muted-foreground);
  }

  &__date {
    font-size: 0.75rem;
    color: var(--v2-muted-foreground);
  }

  &__meta {
    font-size: 0.75rem;
    color: var(--v2-muted-foreground);
    margin-top: var(--v2-space-0-5);
  }
}
</style>
